import * as React from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Container, Hold } from 'types/Watchlist';
import * as Cells from './cells';
import { PresetView } from '../types';
import { ColumnHeader } from './cells/ColumnHeader';
import { Column, OnCellClick } from './cells/types';

const CONTAINER_CELL_WIDTH = 200;
const HOLD_CELL_WIDTH = 150;

const DefaultColDef: Partial<GridColDef> = {
  sortable: false,
  width: CONTAINER_CELL_WIDTH,
  disableColumnMenu: true,
};
const HoldColDef: Partial<GridColDef> = {
  sortable: false,
  width: HOLD_CELL_WIDTH,
  disableColumnMenu: true,
};

/** Helper for writing the hold cell render function once */
const renderHoldCell = (params: GridRenderCellParams) => {
  const hold: Hold | null = params.row.pickupInfo?.[params.field] || null;
  const props = {
    note: hold?.raw || null,
    state: hold?.status || null,
  };
  return <Cells.HoldCell {...props} />;
};

/**
 * This function returns the column definitions for the table
 * @param {string} tooltip The column field to show the tooltip for
 * @param {function} setSortSearchParams The function to call when a column is clicked to sort
 * @param {string} currentSortBy The current sort field
 * @param {string} currentSortDirection The current sort direction
 * @returns {Record<Column, GridColDef>} The column definitions
 */
function getColumnDefs(
  tooltip: string,
  setSortSearchParams: (field: string) => void,
  currentSortBy: string,
  currentSortDirection: string,
  onCellClick?: (container: Container, column: Column) => void
): Record<Column, GridColDef> {
  /** Helper for generating the sort params for a backend-sortable column
   * @param {string} column The column field. NOT from the Column enum above, but the column key the backend expects
   * @returns The sort params
   * */
  const generateSortParams = (column: string) => {
    return {
      showSortArrows: true,
      showSortDirectionArrow: currentSortBy === column ? currentSortDirection : undefined,
      onSort: () => setSortSearchParams(column),
    };
  };

  return {
    [Column.OceanBol]: {
      ...DefaultColDef,
      field: Column.OceanBol,
      renderCell: (params) => <Cells.OceanBOL {...params} onCellClick={onCellClick} />,
      width: 190,
      headerName: 'Ocean BOL',
      renderHeader: () => (
        <ColumnHeader
          name="Ocean BOL"
          tooltip="Your Ocean BOL numbers."
          showTooltip={tooltip === Column.OceanBol}
        />
      ),
    },
    [Column.ContainerNumber]: {
      ...DefaultColDef,
      field: Column.ContainerNumber,
      renderCell: (params) => <Cells.ContainerNumber {...params} onCellClick={onCellClick} />,
      width: 150,
      headerName: 'Container',
      renderHeader: () => (
        <ColumnHeader
          name="Container"
          tooltip="Your container numbers."
          showTooltip={tooltip === Column.ContainerNumber}
        />
      ),
    },
    [Column.State]: {
      ...DefaultColDef,
      field: Column.State,
      renderCell: (params) => <Cells.Status {...params} onCellClick={onCellClick} />,
      width: 150,
      headerName: 'Container status',
      renderHeader: () => (
        <ColumnHeader
          name="Container status"
          tooltip="The current milestone status of where your container is located."
          showTooltip={tooltip === Column.State}
        />
      ),
    },
    [Column.LastKnownStatus]: {
      ...DefaultColDef,
      field: Column.State,
      renderCell: (params) => <Cells.Status {...params} onCellClick={onCellClick} />,
      width: 150,
      headerName: 'Last known status',
      renderHeader: () => (
        <ColumnHeader
          name="Last known status"
          tooltip="The last known milestone status of where your container was located."
          showTooltip={tooltip === Column.LastKnownStatus}
        />
      ),
    },
    [Column.MotherVessel]: {
      ...DefaultColDef,
      field: Column.MotherVessel,
      renderCell: (params) => <Cells.MotherVessel {...params} />,
      headerName: 'Mother vessel',
      renderHeader: () => (
        <ColumnHeader
          name="Mother vessel"
          tooltip="The name of the steamship your container is on."
          showTooltip={tooltip === Column.MotherVessel}
        />
      ),
    },
    [Column.VesselArrival]: {
      ...DefaultColDef,
      field: Column.VesselArrival,
      renderCell: (params) => <Cells.VesselArrival {...params} />,
      width: 160,
      headerName: 'Vessel arrival',
      renderHeader: () => (
        <ColumnHeader
          name="Vessel arrival"
          {...generateSortParams('vessel_arrival')}
          tooltip="The arrival date/time of your container to the port of destination."
          showTooltip={tooltip === Column.VesselArrival}
        />
      ),
    },
    [Column.PickupLocation]: {
      ...DefaultColDef,
      field: Column.PickupLocation,
      renderCell: (params) => <Cells.PickupLocation {...params} />,
      headerName: 'Pickup location',
      renderHeader: () => (
        <ColumnHeader
          name="Pickup location"
          tooltip="The terminal location to pickup your container."
          showTooltip={tooltip === Column.PickupLocation}
        />
      ),
    },
    [Column.LastFreeDay]: {
      ...DefaultColDef,
      field: Column.LastFreeDay,
      headerName: 'LFD/GTD',
      renderCell: (params) => <Cells.LastFreeDayCell {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="LFD/GTD"
          {...generateSortParams('lfd_gtd')}
          tooltip="The last free day (LFD) and good-thru date (GTD) to pickup your container before incurring demurrage charges."
          showTooltip={tooltip === Column.LastFreeDay}
        />
      ),
    },
    [Column.CustomsHold]: {
      ...HoldColDef,
      field: Column.CustomsHold,
      headerName: 'Customs hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="Customs hold"
          tooltip="If the U.S. Customs and Border Protection Agency has placed a hold on your container for further inspection."
          showTooltip={tooltip === Column.CustomsHold}
        />
      ),
    },
    [Column.FreightHold]: {
      ...HoldColDef,
      field: Column.FreightHold,
      headerName: 'Freight hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="Freight hold"
          tooltip="If your container has outstanding freight charges, contact your steamship line to pay/clear this hold."
          showTooltip={tooltip === Column.FreightHold}
        />
      ),
    },
    [Column.ShippingLineHold]: {
      ...HoldColDef,
      field: Column.ShippingLineHold,
      headerName: 'Line hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="Line hold"
          tooltip="If your container is held by the steamship line for any reason (contact them for further details)."
          showTooltip={tooltip === Column.ShippingLineHold}
        />
      ),
    },
    // [Column.usdaHold]: {...HoldColDef, field: Column.usdaHold, renderCell: renderHoldCell, renderHeader: () => <ColumnHeader name="USDA hold" tooltip="If your container is held by USDA for inspection." showTooltip={showTooltip} /> },
    [Column.TmfHold]: {
      ...HoldColDef,
      field: Column.TmfHold,
      headerName: 'TMF hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="TMF hold"
          tooltip="Traffic mitigation fee (TMF) is paid when a terminal becomes congested. Truckers are responsible for paying this hold, which is specific to Los Angeles / Long Beach California terminals."
          showTooltip={tooltip === Column.TmfHold}
        />
      ),
    },
    [Column.CtfHold]: {
      ...HoldColDef,
      field: Column.CtfHold,
      headerName: 'CTF hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="CTF hold"
          tooltip="All containers going through the container terminal gates at the Ports of Los Angeles and Long Beach must pay the Ports' Clean Truck Fund (CTF)."
          showTooltip={tooltip === Column.CtfHold}
        />
      ),
    },
    [Column.TerminalHold]: {
      ...HoldColDef,
      field: Column.TerminalHold,
      headerName: 'Term. hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="Term. hold"
          tooltip="Any hold placed by the terminal. This could be for a variety of reasons, contact the terminal for more details."
          showTooltip={tooltip === Column.TerminalHold}
        />
      ),
    },
    [Column.OtherHold]: {
      ...HoldColDef,
      field: Column.OtherHold,
      headerName: 'Other hold',
      renderCell: renderHoldCell,
      renderHeader: () => (
        <ColumnHeader
          name="Other hold"
          tooltip="Any other holds detected that are not associated with the other defined holds."
          showTooltip={tooltip === Column.OtherHold}
        />
      ),
    },
    [Column.Demurrage]: {
      ...DefaultColDef,
      field: Column.Demurrage,
      headerName: 'Demurrage due',
      renderCell: (params) => <Cells.Demurrage {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Demurrage due"
          {...generateSortParams('demurrage')}
          tooltip="The amount of demurrage charges incurred by your container."
          showTooltip={tooltip === Column.Demurrage}
        />
      ),
    },
    [Column.YardLocation]: {
      ...DefaultColDef,
      field: Column.YardLocation,
      headerName: 'Yard location',
      renderCell: (params) => <Cells.YardLocation {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Yard location"
          tooltip="The terminal yard location of your container."
          showTooltip={tooltip === Column.YardLocation}
        />
      ),
    },
    [Column.PickupAppointment]: {
      ...DefaultColDef,
      field: Column.PickupAppointment,
      headerName: 'Pickup appointment',
      renderCell: (params) => <Cells.PickupAppointment {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Pickup appointment"
          tooltip="The date of the pickup appointment scheduled for this container."
          showTooltip={tooltip === Column.PickupAppointment}
        />
      ),
    },
    [Column.GatedOut]: {
      ...DefaultColDef,
      field: Column.GatedOut,
      headerName: 'Gated out',
      renderCell: (params) => <Cells.GatedOut {...params} />,
      width: 150,
      renderHeader: () => (
        <ColumnHeader
          name="Gated out"
          {...generateSortParams('gated_out')}
          tooltip="When the container was picked up from the terminal."
          showTooltip={tooltip === Column.GatedOut}
        />
      ),
    },
    [Column.EmptyReturned]: {
      ...DefaultColDef,
      field: Column.EmptyReturned,
      headerName: 'Empty returned',
      renderCell: (params) => <Cells.EmptyReturned {...params} />,
      width: 160,
      renderHeader: () => (
        <ColumnHeader
          name="Empty returned"
          tooltip="When the empty container was dropped off."
          showTooltip={tooltip === Column.EmptyReturned}
        />
      ),
    },
    [Column.ReturnLocation]: {
      ...DefaultColDef,
      field: Column.ReturnLocation,
      headerName: 'Return location',
      renderCell: (params) => <Cells.ReturnLocation {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Return location"
          tooltip="Where the container was returned to."
          showTooltip={tooltip === Column.ReturnLocation}
        />
      ),
    },
    [Column.EstPerDiemLFD]: {
      ...DefaultColDef,
      field: Column.EstPerDiemLFD,
      headerName: 'Est. per diem LFD',
      renderCell: (params) => <Cells.EstPerDiemLFD {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Est. per diem LFD"
          {...generateSortParams('est_per_diem_lfd')}
          tooltip="The estimated per diem charges incurred by your container."
          showTooltip={tooltip === Column.EstPerDiemLFD}
        />
      ),
    },
    [Column.EstPerDiemCharges]: {
      ...DefaultColDef,
      field: Column.EstPerDiemCharges,
      headerName: 'Est. per diem charges',
      renderCell: (params) => <Cells.EstPerDiemCharges {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Est. per diem charges"
          tooltip="The estimated per diem charges incurred by your container."
          showTooltip={tooltip === Column.EstPerDiemCharges}
        />
      ),
    },
    [Column.DisputeOpportunity]: {
      ...DefaultColDef,
      field: Column.DisputeOpportunity,
      headerName: 'Disputable Days',
      renderCell: (params) => <Cells.DisputeOpportunity {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Disputable Days"
          tooltip="The estimated percentage of opportunity to dispute the charges."
          showTooltip={tooltip === Column.DisputeOpportunity}
        />
      ),
    },
    [Column.EstSavings]: {
      ...DefaultColDef,
      field: Column.EstSavings,
      headerName: 'Est. savings',
      renderCell: (params) => <Cells.EstSavings {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Est. savings"
          tooltip="The estimated savings if you dispute the charges."
          showTooltip={tooltip === Column.EstSavings}
        />
      ),
    },
    [Column.ReferenceId]: {
      ...DefaultColDef,
      field: Column.ReferenceId,
      headerName: 'Reference ID',
      renderCell: (params) => <Cells.ReferenceId {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Reference ID"
          tooltip="Use the Reference ID as a tag to associate the container with a specific partner, customer, or any other reference that might be helpful."
          showTooltip={tooltip === Column.ReferenceId}
        />
      ),
    },
    [Column.DeletedBy]: {
      ...DefaultColDef,
      field: Column.DeletedBy,
      headerName: 'Deleted by',
      renderCell: (params) => <Cells.DeletedBy {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Deleted by"
          tooltip="The user who deleted the container."
          showTooltip={tooltip === Column.DeletedBy}
        />
      ),
    },
    [Column.DeletedOn]: {
      ...DefaultColDef,
      field: Column.DeletedOn,
      headerName: 'Deleted at',
      renderCell: (params) => <Cells.DeletedOn {...params} />,
      renderHeader: () => (
        <ColumnHeader
          name="Deleted at"
          {...generateSortParams('deleted_at')}
          tooltip="When the container was deleted."
          showTooltip={tooltip === Column.DeletedOn}
        />
      ),
    },
  };
}

export const DEFAULT_SHIPMENT_COLUMNS: Column[] = [
  Column.OceanBol,
  Column.ContainerNumber,
  Column.State,
];

const HOLD_COLUMNS: Column[] = [
  Column.CustomsHold,
  Column.FreightHold,
  Column.ShippingLineHold,
  Column.TmfHold,
  // Column.UsdaHold,
  Column.CtfHold,
  Column.TerminalHold,
  Column.OtherHold,
];

const PER_DIEM_COLUMNS: Column[] = [
  ...DEFAULT_SHIPMENT_COLUMNS,
  Column.PickupLocation,
  Column.GatedOut,
  Column.EstPerDiemLFD,
  Column.EstPerDiemCharges,
  Column.DisputeOpportunity,
  Column.EstSavings,
  Column.ReferenceId,
];

/** Columns used by the "Deleted" view */
const ALL_DELETED_COLUMNS: Column[] = [Column.LastKnownStatus, Column.DeletedOn, Column.DeletedBy];

/** Columns used by the active view */
const ALL_ACTIVE_COLUMNS: Column[] = Object.keys(getColumnDefs('', () => {}, '', '')).filter(
  (k) => !ALL_DELETED_COLUMNS.includes(k as Column)
) as Column[];

/** Which columns to have visible by default for each view */
const presetViewColumns: Record<PresetView, Column[]> = {
  [PresetView.Active]: ALL_ACTIVE_COLUMNS,
  [PresetView.Archive]: ALL_ACTIVE_COLUMNS,
  [PresetView.DemurrageDue]: ALL_ACTIVE_COLUMNS,
  [PresetView.Availability]: [
    ...DEFAULT_SHIPMENT_COLUMNS,
    Column.VesselArrival,
    Column.PickupLocation,
    Column.PickupAppointment,
    Column.LastFreeDay,
    Column.Demurrage,
    Column.YardLocation,
    ...HOLD_COLUMNS,
    Column.ReferenceId,
  ],
  [PresetView.EmptyReturns]: [
    ...DEFAULT_SHIPMENT_COLUMNS,
    Column.PickupLocation,
    Column.GatedOut,
    Column.EstPerDiemLFD,
    Column.EstPerDiemCharges,
    Column.DisputeOpportunity,
    Column.EstSavings,
    Column.ReferenceId,
  ],
  [PresetView.Terminated]: ALL_ACTIVE_COLUMNS,
  [PresetView.WithHolds]: ALL_ACTIVE_COLUMNS,
  [PresetView.AvailableForPickup]: ALL_ACTIVE_COLUMNS,
  [PresetView.OnVessel]: ALL_ACTIVE_COLUMNS,
  [PresetView.OutstandingPerDiem]: PER_DIEM_COLUMNS,
  [PresetView.MoreThanTwoFreeDays]: PER_DIEM_COLUMNS,
  [PresetView.LessThanTwoFreeDays]: PER_DIEM_COLUMNS,
};

export type GetColumns = (
  tooltip?: string,
  setSortSearchParams?: (field: string) => void,
  currentSortBy?: string | null,
  currentSortDirection?: string | null,
  onCellClick?: OnCellClick
) => GridColDef[];

/**
 * This function returns the columns to show in the table, including whether to show the
 * tooltip or not for each column
 * @param tooltip The column to show the tooltip for, if any, otherwise empty string
 * @param setSortSearchParams The function to call when a column is clicked to sort
 * @param currentSortBy The current column being sorted by
 * @param currentSortDirection The current sort direction
 * @returns The columns to show in the table
 */
export const getActiveColumns: GetColumns = (
  tooltip,
  setSortSearchParams,
  currentSortBy,
  currentSortDirection,
  onCellClick
) => {
  const columnDefs = getColumnDefs(
    tooltip || '',
    setSortSearchParams || (() => {}),
    currentSortBy || '',
    currentSortDirection || '',
    onCellClick
  );
  const allColumns = Object.keys(columnDefs).filter((c) =>
    ALL_ACTIVE_COLUMNS.includes(c as Column)
  ) as Column[];
  return allColumns.map((c) => columnDefs[c]);
};

export const getDeletedColumns: GetColumns = (
  tooltip,
  setSortSearchParams,
  currentSortBy,
  currentSortDirection,
  onCellClick
) => {
  const columnDefs = getColumnDefs(
    tooltip || '',
    setSortSearchParams || (() => {}),
    currentSortBy || '',
    currentSortDirection || '',
    onCellClick
  );
  const allowedColumns: Column[] = [
    Column.OceanBol,
    Column.ContainerNumber,
    ...ALL_DELETED_COLUMNS,
  ];
  return allowedColumns.map((c) => ({ ...columnDefs[c], flex: 1 }));
};

export const generateDefaultActiveColumnVisibilityModel = (presetView: PresetView) => {
  // Columns to exclude from toggles
  const exclude = [...DEFAULT_SHIPMENT_COLUMNS];

  // The output model to return
  const model: GridColumnVisibilityModel = {};

  getActiveColumns().forEach((c) => {
    if (exclude.includes(c.field as Column)) {
      return;
    }
    model[c.field] = presetViewColumns[presetView].includes(c.field as Column);
  });

  return model;
};
